import { useEffect, useRef } from "react";
import { ReferenceInput, AutocompleteInput, useDataProvider, useNotify } from "react-admin";
import { useForm, useFormState } from "react-final-form";

// La tabla defaultMaterialProcessing/defaultMaterialFinishing solo guarda un FK
// (rawMaterialCategoryMaterialCategoryId) hacia un PAR ya existente en
// rawMaterialCategoryMaterialCategory. Este componente expone dos buscadores
// independientes (Raw Material Category / Material Category) sin atarlos al
// mismo campo del formulario (bug-3228 / IT-3202), y resuelve el id del par
// real cuando ambos tienen valor.
const PAIR_RESOURCE = "rawMaterialCategoryMaterialCategory";
const RAW_CATEGORY_PICK_SOURCE = "_rawMaterialCategoryPick";
const MATERIAL_CATEGORY_PICK_SOURCE = "_materialCategoryPick";
const PAIR_FIELD = "rawMaterialCategoryMaterialCategoryId";

const RawMaterialCategoryMaterialCategoryInput = ({ record, required = false }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const initializedFromRecord = useRef(false);

  const rawCategoryPick = values[RAW_CATEGORY_PICK_SOURCE];
  const materialCategoryPick = values[MATERIAL_CATEGORY_PICK_SOURCE];

  // Edit: precargar los dos buscadores a partir del par ya guardado en el registro.
  useEffect(() => {
    if (initializedFromRecord.current || !record?.[PAIR_FIELD]) return;
    initializedFromRecord.current = true;

    dataProvider.getOne(PAIR_RESOURCE, { id: record[PAIR_FIELD] })
      .then(({ data }) => {
        if (data?.rawMaterialCategoryId) form.change(RAW_CATEGORY_PICK_SOURCE, data.rawMaterialCategoryId);
        if (data?.materialCategoryId) form.change(MATERIAL_CATEGORY_PICK_SOURCE, data.materialCategoryId);
      })
      .catch(() => {});
  }, [record, dataProvider, form]);

  // Cuando ambos buscadores tienen valor, resolver el par real y setear el
  // campo que efectivamente se envía al backend.
  useEffect(() => {
    if (!rawCategoryPick || !materialCategoryPick) return;

    dataProvider.getList(PAIR_RESOURCE, {
      filter: { rawMaterialCategoryId: rawCategoryPick, materialCategoryId: materialCategoryPick },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    }).then(({ data }) => {
      const pair = data?.[0];
      if (pair) {
        form.change(PAIR_FIELD, pair.id);
      } else {
        form.change(PAIR_FIELD, undefined);
        notify(
          "No existe esa combinación de Raw Material Category y Material Category. Créala primero en Raw material category Material category.",
          "warning"
        );
      }
    });
  }, [rawCategoryPick, materialCategoryPick, dataProvider, form, notify]);

  return (
    <>
      <ReferenceInput
        label="Raw Material Category"
        source={RAW_CATEGORY_PICK_SOURCE}
        reference="rawMaterialCategory"
        filterToQuery={searchText => ({ name: searchText })}
      >
        <AutocompleteInput optionText="name" required={required} />
      </ReferenceInput>
      <ReferenceInput
        label="Material Category"
        source={MATERIAL_CATEGORY_PICK_SOURCE}
        reference="materialsCategory"
        filterToQuery={searchText => ({ name: searchText })}
      >
        <AutocompleteInput optionText="name" required={required} />
      </ReferenceInput>
    </>
  );
};

export default RawMaterialCategoryMaterialCategoryInput;
